import React, { useState, useEffect } from 'react';

const EmployeeIdEditor = () => {
 
  const [currentEmployee, setCurrentEmployee] = useState({
    id: 'RMS3456',
    name: 'John Doe',
    email: 'john.doe@example.com'
  });


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [digits, setDigits] = useState(['', '', '', '']);
  const [availabilityStatus, setAvailabilityStatus] = useState('');
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

 
  const letters = currentEmployee.id.substring(0, 3);
  const currentDigits = currentEmployee.id.substring(3);

 
  const checkIdAvailability = async (newId) => {
   
    await new Promise(resolve => setTimeout(resolve, 500));
    

    const existingIds = ['RMS1234', 'RMS5678', 'RMS9999', 'RMS3456'];
    
    return !existingIds.includes(newId);
  };

  
  const updateEmployeeId = async (oldId, newId) => {
   
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    
    return Math.random() > 0.1;
  };

 
  const handleDigitChange = (index, value) => {
    
    if (value !== '' && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

   
    setAvailabilityStatus('');
    setUpdateMessage('');

  
    if (value && index < 3) {
      const nextInput = document.getElementById(`digit-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

   
    const completedDigits = newDigits.join('');
    if (completedDigits.length === 4) {
      checkAvailability(letters + completedDigits);
    }
  };

  
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // Check ID availability
  const checkAvailability = async (newId) => {
    if (newId === currentEmployee.id) {
      setAvailabilityStatus('current');
      return;
    }

    setIsCheckingAvailability(true);
    try {
      const isAvailable = await checkIdAvailability(newId);
      setAvailabilityStatus(isAvailable ? 'available' : 'exists');
    } catch (error) {
      setAvailabilityStatus('error');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Handle update
  const handleUpdate = async () => {
    const newId = letters + digits.join('');
    setIsUpdating(true);
    setUpdateMessage('');

    try {
      const success = await updateEmployeeId(currentEmployee.id, newId);
      
      if (success) {
        setUpdateMessage('User ID updated.');
        setCurrentEmployee(prev => ({ ...prev, id: newId }));
        // Close modal after successful update
        setTimeout(() => {
          setIsModalOpen(false);
          setUpdateMessage('');
          setAvailabilityStatus('');
          setDigits(['', '', '', '']);
        }, 2000);
      } else {
        setUpdateMessage('Failed to update the User ID.');
      }
    } catch (error) {
      setUpdateMessage('Failed to update the User ID.');
    } finally {
      setIsUpdating(false);
    }
  };


  const openModal = () => {
    setDigits(currentDigits.split(''));
    setIsModalOpen(true);
    setAvailabilityStatus('');
    setUpdateMessage('');
  };


  const closeModal = () => {
    setIsModalOpen(false);
    setDigits(['', '', '', '']);
    setAvailabilityStatus('');
    setUpdateMessage('');
  };

  
  const getStatusMessage = () => {
    if (isCheckingAvailability) {
      return <span className="bg-primary-hrms">Checking availability...</span>;
    }
    
    switch (availabilityStatus) {
      case 'available':
        return <span className="text-green-600">User ID available.</span>;
      case 'exists':
        return <span className="text-red-600">User ID already exists.</span>;
      case 'current':
        return <span className="text-gray-600">Current User ID.</span>;
      case 'error':
        return <span className="text-red-600">Error checking availability.</span>;
      default:
        return null;
    }
  };


  const showUpdateButton = availabilityStatus === 'available' && !isCheckingAvailability;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Employee Card */}
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Employee Details</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-600">Employee ID:</span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-primary-hrms">{currentEmployee.id}</span>
              <button
                onClick={openModal}
                className="px-3 py-1 bg-primary-hrms text-white text-sm rounded hover:bg-green-600 transition-colors"
              >
                Edit
              </button>
            </div>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Name:</span>
            <span>{currentEmployee.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Email:</span>
            <span>{currentEmployee.email}</span>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">Edit Employee ID</h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Employee ID Format: Letters + 4 Digits
              </label>
              
              <div className="flex items-center gap-2 justify-center">
                {/* Fixed Letters */}
                <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-lg font-bold text-gray-600">
                  {letters}
                </div>
                
                <span className="text-gray-400 text-xl">-</span>
                
                {/* Editable Digits */}
                <div className="flex gap-1">
                  {digits.map((digit, index) => (
                    <input
                      key={index}
                      id={`digit-${index}`}
                      type="text"
                      value={digit}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      maxLength={1}
                      className="w-12 h-12 border border-gray-300 rounded text-center text-lg font-mono font-bold focus:border-primary-hrms focus:outline-none focus:ring-2 focus:ring-green-200"
                      placeholder="_"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Status Messages */}
            <div className="mb-4 min-h-6 flex items-center justify-center">
              {getStatusMessage()}
              {updateMessage && (
                <span className={`${updateMessage.includes('updated') ? 'text-green-600' : 'text-red-600'}`}>
                  {updateMessage}
                </span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isUpdating}
              >
                Cancel
              </button>
              
              {showUpdateButton && (
                <button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className={`px-4 py-2 bg-primary-hrms text-white rounded hover:bg-green-600 transition-colors flex items-center gap-2 ${
                    isUpdating ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUpdating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    'Update'
                  )}
                </button>
              )}
            </div>

            {/* Help Text */}
            <div className="mt-4 text-xs text-gray-500 text-center">
              Enter 4 digits to check availability. Only numbers are allowed.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeIdEditor;